import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HeaderMediaControl } from '../../../components/HeaderMediaControl';
import './BHeader.css';

interface BHeaderProps {
  showBack?: boolean;
  showBookmark?: boolean;
  showShare?: boolean;
  showMyStuff?: boolean;
  onBookmark?: () => void;
  onShare?: () => void;
  isBookmarked?: boolean;
}

/**
 * Fixed header with hamburger menu on mobile.
 */
export function BHeader({
  showBack = true,
  showBookmark = false,
  showShare = false,
  showMyStuff = true,
  onBookmark,
  onShare,
  isBookmarked = false,
}: BHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnMyStuff = location.pathname === '/bookmarks';

  const handleHome = () => {
    navigate('/');
  };

  const handleMenuAction = (action: () => void) => {
    action();
    setMenuOpen(false);
  };

  const hasMenuItems = showMyStuff || showBookmark || showShare;

  return (
    <header className="b-header">
      <div className="b-header__left">
        {showBack && (
          <button
            className="b-header__link"
            onClick={handleHome}
          >
            ← Home
          </button>
        )}
      </div>

      <div className="b-header__center">
        <HeaderMediaControl />
      </div>

      {/* Desktop: show all buttons */}
      <div className="b-header__right b-header__right--desktop">
        {showMyStuff && (
          <button
            className={`b-header__link b-header__link--with-icon ${isOnMyStuff ? 'b-header__link--active' : ''}`}
            onClick={() => navigate('/bookmarks')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span>My Stuff</span>
          </button>
        )}
        {showBookmark && (
          <button
            className={`b-header__link b-header__link--with-icon ${isBookmarked ? 'b-header__link--active' : ''}`}
            onClick={onBookmark}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
            <span>{isBookmarked ? 'Saved' : 'Bookmark'}</span>
          </button>
        )}
        {showShare && (
          <button className="b-header__link b-header__link--with-icon" onClick={onShare}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <span>Share</span>
          </button>
        )}
      </div>

      {/* Mobile: hamburger menu */}
      {hasMenuItems && (
        <div className="b-header__right b-header__right--mobile">
          <button
            className={`b-header__hamburger ${menuOpen ? 'b-header__hamburger--open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span />
            <span />
            <span />
          </button>

          {menuOpen && (
            <>
              <div className="b-header__menu-overlay" onClick={() => setMenuOpen(false)} />
              <div className="b-header__menu">
                {showMyStuff && (
                  <button
                    className={`b-header__menu-item ${isOnMyStuff ? 'b-header__menu-item--active' : ''}`}
                    onClick={() => handleMenuAction(() => navigate('/b/bookmarks'))}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    My Stuff
                  </button>
                )}
                {showBookmark && (
                  <button
                    className={`b-header__menu-item ${isBookmarked ? 'b-header__menu-item--active' : ''}`}
                    onClick={() => handleMenuAction(onBookmark || (() => {}))}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                    </svg>
                    {isBookmarked ? 'Saved' : 'Bookmark'}
                  </button>
                )}
                {showShare && (
                  <button
                    className="b-header__menu-item"
                    onClick={() => handleMenuAction(onShare || (() => {}))}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    Share
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
